<!--
  @component StorageIO — P3 effecting lens (L-arc).
  chain:   ctx.deployment.dispatch("storage.*", …) → this → ctx.storage
  effects: provisionBucket/connect + folder ops + share + snapshot/restore
           + teardown — storage deploy workflow + deployable Drive layer.
-->
<script lang="ts" module>
  import { getContext } from "svelte";
  import { STORAGE_IO_KEY } from "./deployment-io.js";
  import type { StorageIOContext } from "./deployment-io.js";
  export { STORAGE_IO_KEY };
  export type { StorageIOContext };
  export function useStorageIO(): StorageIOContext {
    const ctx = getContext<StorageIOContext | undefined>(STORAGE_IO_KEY);
    if (!ctx) throw new Error("useStorageIO() called outside of a StorageIO tree.");
    return ctx;
  }
</script>
<script lang="ts">
  import { setContext, type Snippet } from "svelte";
  import { useDeploymentIO } from "./DeploymentIO.svelte";
  import type { DeployTargetRef } from "./deployment-io.js";

  interface Props { children?: Snippet; }
  const { children }: Props = $props();
  const parent = useDeploymentIO();

  let inFlight = $state(false);
  let lastResult: unknown = $state(null);
  let lastError: Error | null = $state(null);

  async function dispatchOp(op: string, payload: unknown): Promise<unknown> {
    inFlight = true; lastError = null;
    try {
      const r = await parent.dispatch(op, payload);
      lastResult = r;
      return r;
    } catch (e) {
      const err = e instanceof Error ? e : new Error(String(e));
      lastError = err;
      throw err;
    } finally { inFlight = false; }
  }

  const ctx: StorageIOContext = {
    get inFlight() { return inFlight; },
    get lastResult() { return lastResult; },
    get lastError() { return lastError; },
    get history() { return parent.stream.filter((e) => e.op.startsWith("storage.")); },
    provisionBucket: (target: DeployTargetRef, options?) => dispatchOp("storage.provisionBucket", { target, options }),
    connect: (target, options?) => dispatchOp("storage.connect", { target, options }),
    folderCreate: (target, path, options?) => dispatchOp("storage.folderCreate", { target, path, options }),
    folderMove: (target, from, to) => dispatchOp("storage.folderMove", { target, from, to }),
    folderTrash: (target, path) => dispatchOp("storage.folderTrash", { target, path }),
    share: (target, fileRef, options?) => dispatchOp("storage.share", { target, fileRef, options }),
    snapshot: (target, options?) => dispatchOp("storage.snapshot", { target, options }),
    restore: (target, snapshotId, options?) => dispatchOp("storage.restore", { target, snapshotId, options }),
    teardown: (target, options?) => dispatchOp("storage.teardown", { target, options }),
  } satisfies StorageIOContext;

  setContext(STORAGE_IO_KEY, ctx);
</script>
{@render children?.()}
